/*
||--------------------------------------------------------------------------
|| Component: ProductVariantsTable.js
|| Author: Moaz
|| Editing: Sayed Ahmed
|| Description: Renders and manages product variants table including
||              SKU, stock, price, attributes, and image selection.
||              Enhanced with better grid layout, auto-selected images,
||              and improved SKU management.
||--------------------------------------------------------------------------
*/

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  generateSku,
  getAttributeValueFromDisplay,
  calculateFinalPrice,
  formatPrice,
  getDiscountDisplayText,
} from "../../../utils/variantUtils";
import "../../../css/variant-improvements.css";

/* Attribute Badge Group */
const AttributeBadges = ({ productAttributes }) => (
  <>
    {Object.entries(productAttributes).map(([attrId, attr], index) => (
      <div key={`proAttr-${index}-${attrId}`} className="mb-3">
        <h6 className="fw-bold text-muted">{attr.name}</h6>
        <div className="d-flex flex-wrap gap-2">
          {attr.values.map((value) => (
            <span
              key={`${index}-${attrId}-${value.id}`}
              className="badge bg-white text-dark border"
            >
              {value.value}
              {value.hex && (
                <span
                  title={value.value}
                  className="ms-2 rounded-circle"
                  style={{
                    display: "inline-block",
                    width: 16,
                    height: 16,
                    backgroundColor: value.hex,
                    border: "1px solid #aaa",
                    verticalAlign: "middle",
                  }}
                />
              )}
            </span>
          ))}
        </div>
      </div>
    ))}
  </>
);

/* Enhanced Image Selector with Auto-Selection */
const ImageSelector = ({
  imagesListViewr,
  productImages,
  variant,
  onChange,
  autoSelectAll = false,
}) => {
  const imageIdSet = new Set((variant.images || []).map((id) => id.toString()));

  // Auto-select all images when variant is created
  useEffect(() => {
    if (
      autoSelectAll &&
      productImages &&
      productImages.length > 0 &&
      (!variant.images || variant.images.length == 0)
    ) {
      onChange(productImages);
    }
  }, [autoSelectAll, productImages, variant.images, onChange]);

  return (
    <div className="d-flex flex-wrap gap-2">
      {productImages?.map((imgId, indexImg) => {
        const img = imagesListViewr?.find((i) => i.value == imgId);
        const isSelected = imageIdSet.has(imgId.toString());

        const toggleImage = () => {
          const currentImages = variant.images || [];
          const newImages = isSelected
            ? currentImages.filter((id) => id != imgId)
            : [...currentImages, imgId];
          onChange(newImages);
        };

        return (
          <div
            key={`img-${imgId}-${indexImg}`}
            className="position-relative"
            onClick={toggleImage}
            style={{ cursor: "pointer" }}
          >
            <img
              src={img?.src || ""}
              alt="variant"
              className="img-thumbnail shadow-sm"
              style={{
                width: 50,
                height: 50,
                objectFit: "cover",
                border: isSelected ? "2px solid #198754" : "1px solid #ccc",
                borderRadius: "6px",
                transition: "all 0.2s ease-in-out",
                opacity: isSelected ? 1 : 0.6,
              }}
            />
            {isSelected && (
              <div
                className="position-absolute top-0 end-0 bg-success text-white rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: 20,
                  height: 20,
                  fontSize: 12,
                  transform: "translate(25%, -25%)",
                }}
              >
                ✓
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* Enhanced Variant Row with Better Layout */
const VariantRow = ({
  index,
  variant,
  register,
  getValues,
  update,
  watch,
  productAttributes,
  skuCode,
  imagesListViewr,
  productImages,
  baseSku,
  onSkuChange,
  syncEnabled = {},
  onSyncFieldChange,
}) => {
  const [isEditingSku, setIsEditingSku] = useState(false);
  const [customSku, setCustomSku] = useState("");
  const [finalPrice, setFinalPrice] = useState(0);

  // Auto-generate SKU when variant is created or base SKU changes
  useEffect(() => {
    if (baseSku && variant.attribute_values_display) {
      const generatedSku = generateSku(
        baseSku,
        variant.attribute_values_display
      );
      if (!variant.sku || variant.sku == skuCode) {
        update(index, {
          ...getValues(`variants.${index}`),
          sku: generatedSku,
        });
      }
    }
  }, [
    baseSku,
    variant.attribute_values_display,
    index,
    update,
    getValues,
    variant.sku,
    skuCode,
  ]);

  // Calculate final price when price, discount, or discount_type changes
  useEffect(() => {
    const price = variant.price || 0;
    const discount = variant.discount || 0;
    const discountType = variant.discount_type || 'fixed';
    setFinalPrice(calculateFinalPrice(price, discount, discountType));
  }, [variant.price, variant.discount, variant.discount_type]);

  // Watch for form changes and update final price
  const watchedPrice = watch(`variants.${index}.price`);
  const watchedDiscount = watch(`variants.${index}.discount`);
  const watchedDiscountType = watch(`variants.${index}.discount_type`);

  useEffect(() => {
    const price = watchedPrice || 0;
    const discount = watchedDiscount || 0;
    const discountType = watchedDiscountType || 'fixed';
    setFinalPrice(calculateFinalPrice(price, discount, discountType));
  }, [watchedPrice, watchedDiscount, watchedDiscountType]);

  // Update variant data when form values change
  useEffect(() => {
    if (watchedPrice !== undefined || watchedDiscount !== undefined || watchedDiscountType !== undefined) {
      const currentVariant = getValues(`variants.${index}`);
      const updatedVariant = {
        ...currentVariant,
        price: watchedPrice !== undefined ? watchedPrice : currentVariant.price || 0,
        discount: watchedDiscount !== undefined ? watchedDiscount : currentVariant.discount || 0,
        discount_type: watchedDiscountType !== undefined ? watchedDiscountType : currentVariant.discount_type || 'fixed',
      };
      
      // Only update if there are actual changes
      if (updatedVariant.price !== currentVariant.price || 
          updatedVariant.discount !== currentVariant.discount || 
          updatedVariant.discount_type !== currentVariant.discount_type) {
        update(index, updatedVariant);
      }
    }
  }, [watchedPrice, watchedDiscount, watchedDiscountType, index, update, getValues]);

  const handleImageChange = (newImages) => {
    update(index, {
      ...getValues(`variants.${index}`),
      images: newImages,
    });
  };

  const handleSkuEdit = () => {
    setCustomSku(variant.sku || "");
    setIsEditingSku(true);
  };

  const handleSkuSave = () => {
    update(index, {
      ...getValues(`variants.${index}`),
      sku: customSku,
    });
    setIsEditingSku(false);
    onSkuChange?.(customSku);
  };

  const handleSkuCancel = () => {
    setCustomSku("");
    setIsEditingSku(false);
  };

  const handleSkuRegenerate = () => {
    const generatedSku = generateSku(baseSku, variant.attribute_values_display);
    update(index, {
      ...getValues(`variants.${index}`),
      sku: generatedSku,
    });
    onSkuChange?.(generatedSku);
  };

  return (
    <div className="variant-card mb-3 p-3 border rounded-3">
      <div className="row g-3">
        {/* SKU Section */}
        <div className="col-12 col-md-6 col-lg-4">
          <label className="form-label fw-semibold text-muted small">SKU</label>
          <div className="input-group">
            {isEditingSku ? (
              <>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={customSku}
                  onChange={(e) => setCustomSku(e.target.value)}
                  placeholder="Enter custom SKU"
                />
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={handleSkuSave}
                >
                  <i className="fas fa-check"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleSkuCancel}
                >
                  <i className="fas fa-times"></i>
                </button>
              </>
            ) : (
              <>
                <input
                  type="text"
                  className="form-control form-control-sm form-control-enhanced"
                  value={variant.sku || ""}
                  readOnly
                  {...register(`variants.${index}.sku`)}
                />
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm btn-enhanced"
                  onClick={handleSkuEdit}
                  title="Edit SKU"
                >
                  <i className="bi bi-pencil icon-enhanced"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm btn-enhanced"
                  onClick={handleSkuRegenerate}
                  title="Regenerate SKU"
                >
                  <i className="bi bi-arrow-repeat icon-enhanced"></i>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Price Section */}
        <div className="col-12 col-md-6 col-lg-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="form-label fw-semibold text-muted small mb-0">
              Price
            </label>
            {syncEnabled.price && (
              <span className="sync-indicator" title="Synced with Product">
                <i className="fas fa-link me-1"></i>
                Synced
              </span>
            )}
          </div>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              type="number"
              className={`form-control form-control-sm form-control-enhanced ${syncEnabled.price ? 'synced-field' : ''}`}
              step="0.01"
              {...register(`variants.${index}.price`)}
            />
          </div>
          {/* Final Price Display */}
          {variant.discount && variant.discount > 0 && (
            <div className="mt-1 price-comparison">
              <span className="original-price">{formatPrice(variant.price || 0)}</span>
              <span className="final-price">{formatPrice(finalPrice)}</span>
              <span className="discount-percentage">
                {variant.discount_type == 'percentage' ? `${variant.discount}%` : `${variant.discount}$`} OFF
              </span>
            </div>
          )}
        </div>

        {/* Discount Section */}
        <div className="col-12 col-md-6 col-lg-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="form-label fw-semibold text-muted small mb-0">
              Discount
            </label>
            {syncEnabled.discount && (
              <span className="sync-indicator" title="Synced with Product">
                <i className="fas fa-link me-1"></i>
                Synced
              </span>
            )}
          </div>
          <input
            type="number"
            className={`form-control form-control-sm form-control-enhanced ${syncEnabled.discount ? 'synced-field' : ''}`}
            step="0.01"
            min="0"
            {...register(`variants.${index}.discount`)}
          />
          {/* Discount Display */}
          {variant.discount && variant.discount > 0 && (
            <div className="mt-1">
              <span className="discount-badge">
                {getDiscountDisplayText(variant.discount, variant.discount_type || 'fixed')}
              </span>
            </div>
          )}
        </div>

        {/* Discount Type Section */}
        <div className="col-12 col-md-6 col-lg-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="form-label fw-semibold text-muted small mb-0">
              Discount Type
            </label>
            {syncEnabled.discountType && (
              <span className="sync-indicator" title="Synced with Product">
                <i className="fas fa-link me-1"></i>
                Synced
              </span>
            )}
          </div>
          <select
            className={`form-select form-select-sm form-control-enhanced ${syncEnabled.discountType ? 'synced-field' : ''}`}
            {...register(`variants.${index}.discount_type`)}
          >
            <option value="fixed">Fixed</option>
            <option value="percentage">Percentage</option>
          </select>
        </div>

        {/* Stock Section */}
        <div className="col-12 col-md-6 col-lg-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="form-label fw-semibold text-muted small mb-0">
              Stock
            </label>
            {syncEnabled.stock && (
              <span className="sync-indicator" title="Synced with Product">
                <i className="fas fa-link me-1"></i>
                Synced
              </span>
            )}
          </div>
          <input
            type="number"
            className={`form-control form-control-sm form-control-enhanced ${syncEnabled.stock ? 'synced-field' : ''}`}
            min="0"
            {...register(`variants.${index}.stock`)}
          />
        </div>

        {/* Attributes Section */}
        <div className="col-12">
          <label className="form-label fw-semibold text-muted small">
            Attributes
          </label>
          <div className="d-flex flex-wrap gap-2">
            {Object.keys(productAttributes).map((attrId, proAttrIndex) => {
              const attrValue = getAttributeValueFromDisplay(variant, attrId);
              return (
                <div
                  key={`value-${proAttrIndex}-${attrId}`}
                  className="badge bg-primary"
                >
                  <strong>{productAttributes[attrId].name}:</strong>{" "}
                  {attrValue?.value || ""}
                  {attrValue?.hex && (
                    <span
                      className="ms-2 rounded-circle"
                      style={{
                        display: "inline-block",
                        width: 12,
                        height: 12,
                        backgroundColor: attrValue.hex,
                        border: "1px solid #fff",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Images Section */}
        <div className="col-12">
          <label className="form-label fw-semibold text-muted small">
            Images
          </label>
          <ImageSelector
            imagesListViewr={imagesListViewr}
            productImages={productImages}
            variant={variant}
            onChange={handleImageChange}
            autoSelectAll={true}
          />
        </div>
      </div>
    </div>
  );
};

/* Main Table Component with Enhanced Grid Layout */
export default function ProductVariantsTable({
  productAttributes,
  variantFields,
  productImages,
  imagesListViewr,
  register,
  getValues,
  update,
  watch,
  skuCode,
  syncEnabled = {},
  onSyncFieldChange,
}) {
  const { t } = useTranslation();
  const [baseSku, setBaseSku] = useState(skuCode);

  // Listen for base SKU changes
  useEffect(() => {
    setBaseSku(skuCode);
  }, [skuCode]);

  const handleSkuChange = (newSku) => {
    // This can be used to notify parent component of SKU changes
    console.log("Variant SKU changed:", newSku);
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">
            <i className="fas fa-layer-group me-2 text-primary"></i>
            {t("Product Variants")}
          </h4>
          <span className="badge bg-primary fs-6">
            {variantFields.length} {t("Variants")}
          </span>
        </div>

        {/* Attribute Display */}
        <div className="mb-4">
          <h6 className="fw-bold text-muted mb-3">
            <i className="fas fa-tags me-2"></i>
            {t("Selected Attributes")}
          </h6>
          <AttributeBadges productAttributes={productAttributes} />
        </div>

        {/* Variants Grid */}
        <div className="variants-grid">
          <h6 className="fw-bold text-muted mb-3">
            <i className="fas fa-list me-2"></i>
            {t("Variant Details")}
          </h6>

          {variantFields.length == 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-info-circle fa-3x mb-3"></i>
              <p className="mb-0">
                {t(
                  "No variants available. Please select attributes and their values."
                )}
              </p>
            </div>
          ) : (
            <div className="row g-3">
              {variantFields.map((variant, index) => (
                <div key={variant._key} className="col-12">
                  <VariantRow
                    index={index}
                    variant={variant}
                    register={register}
                    getValues={getValues}
                    update={update}
                    watch={watch}
                    productAttributes={productAttributes}
                    skuCode={skuCode}
                    imagesListViewr={imagesListViewr}
                    productImages={productImages}
                    baseSku={baseSku}
                    onSkuChange={handleSkuChange}
                    syncEnabled={syncEnabled}
                    onSyncFieldChange={onSyncFieldChange}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary Stats */}
        {variantFields.length > 0 && (
          <div className="mt-4 p-3 rounded-3 summary-stats">
            <div className="row text-center">
              <div className="col-3">
                <div className="fw-bold text-primary fs-5">
                  {variantFields.length}
                </div>
                <div className="text-muted small">{t("Total Variants")}</div>
              </div>
              <div className="col-3">
                <div className="fw-bold text-success fs-5">
                  {
                    variantFields.filter((v) => v.images && v.images.length > 0)
                      .length
                  }
                </div>
                <div className="text-muted small">{t("With Images")}</div>
              </div>
              <div className="col-3">
                <div className="fw-bold text-warning fs-5">
                  {
                    variantFields.filter((v) => v.discount && v.discount > 0)
                      .length
                  }
                </div>
                <div className="text-muted small">{t("With Discount")}</div>
              </div>
              <div className="col-3">
                <div className="fw-bold text-info fs-5">
                  {Object.keys(productAttributes).length}
                </div>
                <div className="text-muted small">{t("Attributes")}</div>
              </div>
            </div>
            
            {/* Sync Status */}
            {Object.values(syncEnabled).some(enabled => enabled) && (
              <div className="mt-3 pt-3 border-top">
                <div className="d-flex justify-content-center align-items-center gap-3">
                  <span className="text-muted small">
                    <i className="fas fa-sync me-1"></i>
                    {t("Sync Status")}:
                  </span>
                  {syncEnabled.price && (
                    <span className="sync-indicator">
                      <i className="fas fa-dollar-sign me-1"></i>
                      {t("Price")}
                    </span>
                  )}
                  {syncEnabled.discount && (
                    <span className="sync-indicator">
                      <i className="fas fa-percentage me-1"></i>
                      {t("Discount")}
                    </span>
                  )}
                  {syncEnabled.discountType && (
                    <span className="sync-indicator">
                      <i className="fas fa-tag me-1"></i>
                      {t("Discount Type")}
                    </span>
                  )}
                  {syncEnabled.stock && (
                    <span className="sync-indicator">
                      <i className="fas fa-boxes me-1"></i>
                      {t("Stock")}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
